import { Dispatch, SetStateAction, useState } from "react"
import useSWRImmutable from "swr/immutable"

import { EVACDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import {
    getEmptyEVACDeviceFormData,
    mapEVACDeviceResponseToDeviceFormData,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_AMENDMENT_CONFIG } from "../../../swrConfig"
import { NewEvacDeviceAmendmentResponse } from "../../../types/devices/lct/publishEvacDeviceTypes"
import { START_NEW_LCT_DEVICE_AMENDMENT } from "../../../urls/private"

interface IUseStartNewEvacDeviceAmendmentOutput {
    data: EVACDeviceFormData
    setData: Dispatch<SetStateAction<EVACDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    error?: AppError
}

const startNewEvacDeviceAmendmentMapper = getTsIoMapper(
    NewEvacDeviceAmendmentResponse,
    "startNewEvacDeviceAmendmentMapper"
)

/**
 * Hook for starting a new EVAC device amendment.
 * Returns data for the device to amend, which is then used to populate the device context
 */
const useStartNewEvacDeviceAmendment = (
    deviceRef: string,
    attachmentUUID: string
): IUseStartNewEvacDeviceAmendmentOutput => {
    const [data, setData] = useState<EVACDeviceFormData>(
        getEmptyEVACDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_AMENDMENT(
            deviceRef,
            attachmentUUID,
            LCTOnlyDatasets.EVAC
        ),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes =
                    startNewEvacDeviceAmendmentMapper.map(response)
                setData(mapEVACDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError(
                    "Failed to start new EVAC amendment - data load failed"
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_AMENDMENT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewEvacDeviceAmendment
